import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminAuthAPI } from '../services/adminApi';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
} from '../utils/safeStorage';
import { setAdminTenantInfo, clearAdminTenantInfo, getAdminTenantInfo } from '../utils/tenantHelper';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if admin is logged in on app start
    checkAdminAuthStatus();
    // Restore admin tenant from localStorage (separate from team tenant)
    const savedTenant = getAdminTenantInfo();
    if (savedTenant) {
      setTenant(savedTenant);
    }
  }, []);

  const checkAdminAuthStatus = async () => {
    try {
      const token = getLocalStorageItem('adminToken');
      const user = getLocalStorageItem('adminUser');
      
      if (!token || !user) {
        setIsLoading(false);
        return;
      }

      // Verify token with server and merge profile (e.g. isSuperAdmin) into stored user
      const response = await adminAuthAPI.getProfile();
      if (response && response.success && response.user) {
        const parsedUser = JSON.parse(user);
        setAdminUser({ ...parsedUser, ...response.user });
      } else if (response && response.success) {
        const parsedUser = JSON.parse(user);
        setAdminUser(parsedUser);
      } else {
        // Token is invalid, clear it
        removeLocalStorageItem('adminToken');
        removeLocalStorageItem('adminUser');
      }
    } catch (error) {
      console.error('Admin auth check failed:', error);
      // Token is invalid or expired, clear it
      removeLocalStorageItem('adminToken');
      removeLocalStorageItem('adminUser');
    } finally {
      setIsLoading(false);
    }
  };

  const login = (user, token, tenantData = null) => {
    setLocalStorageItem('adminToken', token);
    setLocalStorageItem('adminUser', JSON.stringify(user));
    setAdminUser(user);
    
    // Save admin tenant for API calls (does not overwrite team tenant)
    if (tenantData) {
      console.log('🏢 Admin: Storing admin tenant info:', tenantData);
      setAdminTenantInfo(tenantData);
      setTenant(tenantData);
    }
  };

  const logout = () => {
    removeLocalStorageItem('adminToken');
    removeLocalStorageItem('adminUser');
    clearAdminTenantInfo();
    setAdminUser(null);
    setTenant(null);
  };

  const updateUser = (user) => {
    if (user && adminUser) {
      const merged = { ...adminUser, ...user };
      setLocalStorageItem('adminUser', JSON.stringify(merged));
      setAdminUser(merged);
    }
  };

  const isAuthenticated = !!adminUser;

  const value = {
    adminUser,
    tenant,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateUser
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
